/**
 * PTW Console & Navigation Overhaul (Task #2196) — PTW route slice.
 * PTW Console Split (Task #3013) — My Work, All Permits, Dashboard,
 * Isolations and Bulk Export as standalone pages.
 *
 * Covers: unified Permit Console, audit explorer, onboarding wizard,
 * reviews queue, chain health report, PTW dashboards, permit detail,
 * configure stub, and all alias-redirects.
 * Order matters — specific paths before :permitId.
 */
import { lazy } from "react";
import { Route, useSearch, useLocation } from "wouter";
import { useEffect } from "react";
import {
  SuperAdminRoute,
  FeatureGuardedRoute,
} from "@/components/routing/RouteGate";
import { AliasRedirect } from "@/components/routing/AliasRedirect";

/**
 * Task #3013 — Translate legacy `/ptw?tab=X` deep-links to the new per-page URLs.
 * Maps: my_queue → /actions/my-work?tab=permits, all_permits → /ptw/all-permits,
 *       dashboards → /ptw/dashboard
 * Passes through: filter, q, drawer query params where relevant.
 *
 * Task #3117 — my_queue now redirects to /actions/my-work?tab=permits so the
 * unified My Work page is the single entry point for permit queue work.
 */
function PtwTabRedirect() {
  const search = useSearch();
  const [, setLocation] = useLocation();
  useEffect(() => {
    const p = new URLSearchParams(search ?? "");
    const tab = p.get("tab");
    const filter = p.get("filter");
    const q = p.get("q");
    const drawer = p.get("drawer");

    if (tab === "my_queue" || tab === null || tab === "") {
      const out = new URLSearchParams();
      out.set("tab", "permits");
      if (filter) out.set("ptwFilter", filter);
      if (q) out.set("ptwQ", q);
      setLocation(`/actions/my-work?${out.toString()}`, { replace: true });
      return;
    }

    if (tab === "templates") {
      setLocation("/ptw/library", { replace: true });
      return;
    }

    let dest = "/ptw/all-permits";
    if (tab === "dashboards") dest = "/dashboard";

    const out = new URLSearchParams();
    if (filter) out.set("filter", filter);
    if (q) out.set("q", q);
    if (drawer && tab === "all_permits") out.set("drawer", drawer);
    const qs = out.toString();
    setLocation(qs ? `${dest}?${qs}` : dest, { replace: true });
  }, [search, setLocation]);
  return null;
}

/**
 * Task #3117 — /ptw/my-work now redirects to /actions/my-work?tab=permits.
 * The component is retained for backward-compat imports but the route uses
 * a lightweight redirect so no bundle chunk is eagerly loaded.
 */
function PtwMyWorkRedirect() {
  const search = useSearch();
  const [, setLocation] = useLocation();
  useEffect(() => {
    const p = new URLSearchParams(search ?? "");
    const filter = p.get("filter");
    const q = p.get("q");
    const out = new URLSearchParams();
    out.set("tab", "permits");
    if (filter) out.set("ptwFilter", filter);
    if (q) out.set("ptwQ", q);
    setLocation(`/actions/my-work?${out.toString()}`, { replace: true });
  }, [search, setLocation]);
  return null;
}
const PtwAllPermitsPage = lazy(() => import("@/pages/ptw/PtwAllPermitsPage"));
const PtwDashboardPage = lazy(() => import("@/pages/ptw/PtwDashboardPage"));
const PtwIsolationsPage = lazy(() => import("@/pages/ptw/PtwIsolationsPage"));
const PtwBulkExportPage = lazy(() => import("@/pages/ptw/PtwBulkExportPage"));
const PermitCreatePage = lazy(() => import("@/pages/ptw/PermitCreatePage"));
const WorkflowConfigPTW = lazy(() => import("@/pages/super-admin/WorkflowConfigPTW"));
const SuperAdminPtwPage = lazy(() => import("@/pages/super-admin/SuperAdminPtwPage"));
const PtwNotificationOutboxPage = lazy(() => import("@/pages/super-admin/PtwNotificationOutboxPage"));
const PermitDetailUnified = lazy(() => import("@/pages/ptw/PermitDetailUnified"));
const PtwAudit = lazy(() => import("@/pages/ptw/PtwAudit"));
const PtwOnboardingWizard = lazy(() => import("@/pages/ptw/standalone/PtwOnboardingWizard"));
const PtwReviews = lazy(() => import("@/pages/ptw/PtwReviews"));
const PtwChainHealthReport = lazy(() => import("@/pages/ptw/reports/ChainHealthReport"));
const PtwDashboards = lazy(() => import("@/pages/ptw/dashboards/PtwDashboards"));
const PtwLeadingIndicatorsPage = lazy(() => import("@/pages/ptw/PtwLeadingIndicatorsPage"));
const PTWConfigurePage = lazy(() => import("@/pages/ptw/PTWConfigurePage"));
const CompetencyMatrix = lazy(() => import("@/pages/ptw/CompetencyMatrix"));
const PTWTemplateEditorPage = lazy(() => import("@/pages/ptw/PTWTemplateEditorPage"));
const PTWOperationsView = lazy(() => import("@/pages/ptw/PTWOperationsView"));
const SuperAdminPtwBlueprintsPage = lazy(() => import("@/pages/super-admin/SuperAdminPtwBlueprintsPage"));
const PtwBlueprintDriftReport = lazy(() => import("@/pages/super-admin/PtwBlueprintDriftReport"));
const VerifyPermit = lazy(() => import("@/pages/ptw/VerifyPermit"));
const PtwLibraryPage = lazy(() => import("@/pages/ptw/PtwLibraryPage"));
const PtwWebhooksPage = lazy(() => import("@/pages/ptw/PtwWebhooksPage"));
const PtwSimopsSpatialPage = lazy(() => import("@/pages/ptw/PtwSimopsSpatialPage"));
const SimopsCommandCenterPage = lazy(() => import("@/pages/ptw/SimopsCommandCenterPage"));

export function PtwRoutes() {
  return (
    <>
      {/* Task #3013 — /ptw (bare) → translate legacy ?tab= params then redirect */}
      <Route path="/ptw">{() => <PtwTabRedirect />}</Route>

      {/* Task #3117 — /ptw/my-work now redirects to /actions/my-work?tab=permits */}
      <Route path="/ptw/my-work">{() => <PtwMyWorkRedirect />}</Route>
      <Route path="/ptw/all-permits">{() => <FeatureGuardedRoute feature="operations.ptw.basicPermits" permission="ptw.view" component={PtwAllPermitsPage} />}</Route>
      <Route path="/ptw/dashboard">{() => <AliasRedirect from="/ptw/dashboard" to="/dashboard" />}</Route>
      {/* PTW-AUD-113 — the isolations register is gated on the base isolation
          add-on, not the long-term-isolation add-on (LTI is a sub-tab inside). */}
      <Route path="/ptw/isolations">{() => <FeatureGuardedRoute feature="operations.ptw.isolationManagement" permission="ptw.view" component={PtwIsolationsPage} />}</Route>
      {/* PTW-AUD-111 — bulk export is gated on the bulkExport add-on and the
          dedicated ptw.export permission (not ptw.view). */}
      <Route path="/ptw/export">{() => <FeatureGuardedRoute feature="operations.ptw.bulkExport" permission="ptw.export" component={PtwBulkExportPage} />}</Route>

      <Route path="/ptw/verify">{() => <FeatureGuardedRoute feature="operations.ptw.basicPermits" permission="ptw.view" component={VerifyPermit} />}</Route>
      <Route path="/ptw/operations">{() => <FeatureGuardedRoute feature="operations.ptw.basicPermits" permission="ptw.view" component={PTWOperationsView} />}</Route>
      {/* PTW-AUD-105 — audit explorer is read-only; open it to holders of
          ptw.view (Viewer/Auditor) as well as ptw.review (OR semantics). */}
      <Route path="/ptw/audit">{() => <FeatureGuardedRoute feature="operations.ptw.basicPermits" permission={["ptw.view", "ptw.review"]} component={PtwAudit} />}</Route>
      <Route path="/ptw/onboarding">{() => <SuperAdminRoute component={PtwOnboardingWizard} />}</Route>
      <Route path="/ptw/reviews">{() => <FeatureGuardedRoute feature="operations.ptw.basicPermits" permission="ptw.review" component={PtwReviews} />}</Route>
      <Route path="/ptw/reports/chain">{() => <FeatureGuardedRoute feature="operations.ptw.basicPermits" permission="ptw.review" component={PtwChainHealthReport} />}</Route>
      <Route path="/ptw/dashboards">{() => <FeatureGuardedRoute feature="analytics.basic" permission="ptw.view" component={PtwDashboards} />}</Route>
      {/* PTW-AUD-710 — leading-indicator safety-analytics surface */}
      <Route path="/ptw/leading-indicators">{() => <FeatureGuardedRoute feature="analytics.basic" permission="ptw.view" component={PtwLeadingIndicatorsPage} />}</Route>
      {/* Task #2555 — Tenant-facing Template Library browse page */}
      {/* PTW-AUD-108 — read-only library browse gated on ptw.configure so Site Managers can browse; install/mutation stays ptw.manage_templates server-side */}
      <Route path="/ptw/library">{() => <FeatureGuardedRoute feature="operations.ptw.basicPermits" permission="ptw.configure" component={PtwLibraryPage} />}</Route>
      {/* Step 11 — /ptw/configure stub route (contents in template/config task) */}
      <Route path="/ptw/configure">{() => <FeatureGuardedRoute feature="operations.ptw.basicPermits" permission="ptw.configure" component={PTWConfigurePage} />}</Route>
      {/* Task #2201 — Template editor at its own URL for deep-linking */}
      <Route path="/ptw/configure/templates/:id">{() => <FeatureGuardedRoute feature="operations.ptw.basicPermits" permission="ptw.manage_templates" component={PTWTemplateEditorPage} />}</Route>
      {/* PTW-AUD-714 — per-permit-type competency / training matrix admin */}
      <Route path="/ptw/competency-matrix">{() => <FeatureGuardedRoute feature="operations.ptw.basicPermits" permission="ptw.configure" component={CompetencyMatrix} />}</Route>
      {/* PTW-AUD-860 — per-tenant outbound webhook admin (gated like sibling PTW admin pages) */}
      <Route path="/ptw/webhooks">{() => <FeatureGuardedRoute feature="operations.ptw.basicPermits" permission="ptw.configure" component={PtwWebhooksPage} />}</Route>
      {/* SA PTW Blueprint management page */}
      <Route path="/super-admin/ptw/blueprints">{() => <SuperAdminRoute component={SuperAdminPtwBlueprintsPage} />}</Route>
      {/* Task #3806 — SA Blueprint Drift Report: governance view for cascade health */}
      <Route path="/super-admin/ptw/blueprint-drift">{() => <SuperAdminRoute component={PtwBlueprintDriftReport} />}</Route>
      {/* Task #3097 — PTW Notification Outbox: surface undelivered permit notifications */}
      <Route path="/super-admin/ptw/notification-outbox">{() => <SuperAdminRoute component={PtwNotificationOutboxPage} />}</Route>
      <Route path="/ptw/create">{() => <FeatureGuardedRoute feature="operations.ptw.basicPermits" permission="ptw.create" component={PermitCreatePage} />}</Route>
      <Route path="/ptw/new">{() => <AliasRedirect from="/ptw/new" to="/ptw/create" />}</Route>
      {/* Task #3833 — /ptw/simops is now the SIMOPS Command Center hub page.
          The old alias-redirect to /ptw/all-permits?drawer=simops is gone. */}
      <Route path="/ptw/simops">{() => <FeatureGuardedRoute feature="operations.ptw.simultaneousOperations" permission="ptw.view" component={SimopsCommandCenterPage} />}</Route>
      {/* PTW-AUD-713 — spatial / site-plan SIMOPS conflict view */}
      <Route path="/ptw/simops/spatial">{() => <FeatureGuardedRoute feature="operations.ptw.simultaneousOperations" permission="ptw.view" component={PtwSimopsSpatialPage} />}</Route>
      <Route path="/ptw/shift-handover">{() => <FeatureGuardedRoute feature="operations.ptw.shiftHandover" permission="ptw.view" component={AliasRedirect} componentProps={{ from: "/ptw/shift-handover", to: "/supervisor/shift-handover" }} />}</Route>
      {/* Templates and Settings are now at /ptw/configure — deep-link via ?tab= so */}
      {/* the configure page opens on the correct panel. */}
      <Route path="/ptw/workflows">{() => <AliasRedirect from="/ptw/workflows" to="/ptw/library" />}</Route>
      <Route path="/ptw/capacity">{() => <AliasRedirect from="/ptw/capacity" to="/dashboard" />}</Route>
      <Route path="/ptw/templates">{() => <AliasRedirect from="/ptw/templates" to="/ptw/library" />}</Route>
      <Route path="/ptw/settings">{() => <AliasRedirect from="/ptw/settings" to="/ptw/configure?tab=settings" />}</Route>
      <Route path="/ptw/returned">{() => <AliasRedirect from="/ptw/returned" to="/ptw/my-work?filter=returned" />}</Route>
      {/* Task #3117 — /ptw/my-queue redirects to /actions/my-work?tab=permits */}
      <Route path="/ptw/my-queue">{() => <AliasRedirect from="/ptw/my-queue" to="/actions/my-work?tab=permits" />}</Route>
      <Route path="/ptw/pdf-defaults">{() => <AliasRedirect from="/ptw/pdf-defaults" to="/ptw/configure?tab=settings&section=pdf" />}</Route>
      <Route path="/ptw/numbering">{() => <AliasRedirect from="/ptw/numbering" to="/ptw/configure?section=numbering" />}</Route>
      <Route path="/ptw/sandbox">{() => <FeatureGuardedRoute feature="platform.devSandbox" permission="ptw.manage_templates" component={AliasRedirect} componentProps={{ from: "/ptw/sandbox", to: "/ptw/configure?section=sandbox" }} />}</Route>
      <Route path="/ptw/permits/:permitId">{(params: { permitId: string }) => <AliasRedirect from={`/ptw/permits/${params.permitId}`} to={`/ptw/${params.permitId}`} />}</Route>
      <Route path="/ptw/:permitId">{() => <FeatureGuardedRoute feature="operations.ptw.basicPermits" permission="ptw.view" component={PermitDetailUnified} />}</Route>
      <Route path="/super-admin/workflow-config/ptw">{() => <SuperAdminRoute component={WorkflowConfigPTW} />}</Route>
      <Route path="/super-admin/ptw">{() => <SuperAdminRoute component={SuperAdminPtwPage} />}</Route>
    </>
  );
}
